import { CheckCircle2, ChevronDown, ChevronUp, Lock, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Incident, IncidentStatus } from "./types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Props = {
  incidents: Incident[];
  onSelect: (id: string) => void;
  onDelete?: (id: string) => void;
  onStatusChange?: (id: string, status: IncidentStatus) => void;
  showAll: boolean;
  onToggleShowAll: () => void;
  hasMore: boolean;
  showCreator?: boolean;
};

const STATUS_OPTIONS: { value: IncidentStatus; label: string }[] = [
  { value: "intake", label: "Intake" },
  { value: "coaching", label: "Coaching" },
  { value: "followup", label: "Follow-up" },
  { value: "paused", label: "Paused" },
  { value: "closed", label: "Closed" },
];

const statusLabel = (s: IncidentStatus) =>
  s === "followup"
    ? "Follow-up"
    : s === "paused"
      ? "Paused"
      : s.charAt(0).toUpperCase() + s.slice(1);

const statusClasses = (s: IncidentStatus) =>
  s === "closed"
    ? "bg-green-100 text-green-700"
    : s === "paused"
      ? "bg-purple-50 text-purple-700"
      : s === "followup"
        ? "bg-blue-50 text-blue-700"
        : s === "coaching"
          ? "bg-amber-50 text-amber-700"
          : "bg-gray-100 text-gray-600";

const statusHoverClasses = (s: IncidentStatus) =>
  s === "closed"
    ? "hover:bg-green-200"
    : s === "paused"
      ? "hover:bg-purple-100"
      : s === "followup"
        ? "hover:bg-blue-100"
        : s === "coaching"
          ? "hover:bg-amber-100"
          : "hover:bg-gray-200";

export const IncidentList = ({
  incidents,
  onSelect,
  onDelete,
  onStatusChange,
  showAll,
  onToggleShowAll,
  hasMore,
  showCreator = false,
}: Props) => {
  if (incidents.length === 0) {
    return <p className="text-sm text-gray-400 py-6 text-center">No incidents match this filter.</p>;
  }

  return (
    <>
      <div className="space-y-2">
        {incidents.map((inc) => (
          <div
            key={inc.id}
            className="flex items-center gap-2 w-full bg-gray-50 border border-gray-200 rounded-xl overflow-hidden hover:bg-white hover:shadow-sm transition-all group"
          >
            <button
              type="button"
              onClick={() => onSelect(inc.id)}
              className="flex-1 min-w-0 text-left px-4 py-3"
            >
              <div className="font-medium text-sm text-gray-800">
                {inc.student_identifier || "Unnamed student"}
                {inc.grade_level ? ` · Grade ${inc.grade_level}` : ""}
              </div>
              <div className="text-xs text-gray-500 mt-1.5 flex items-center gap-2">
                <span>{new Date(inc.created_at).toLocaleDateString()}</span>
                <span className="text-gray-300">·</span>
                <span className="truncate">{inc.incident_description?.slice(0, 70) || "No description"}</span>
                {(inc.share_scope ?? "school") === "private" && (
                  <span
                    className="inline-flex items-center gap-0.5 text-amber-600 flex-shrink-0"
                    title="Private — not visible to colleagues"
                  >
                    <Lock className="w-3 h-3" aria-hidden />
                    <span>Private</span>
                  </span>
                )}
              </div>
              {showCreator && inc.creator_name && (
                <div className="text-xs text-gray-400 mt-1">
                  Created by {inc.creator_name}
                </div>
              )}
            </button>

            {onStatusChange ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => e.stopPropagation()}
                    className={cn(
                      "text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 inline-flex items-center gap-1 transition-colors focus:outline-none focus:ring-2 focus:ring-bodhi-teal/40",
                      statusClasses(inc.status),
                      statusHoverClasses(inc.status),
                    )}
                    title="Change status"
                    aria-label={`Change status for ${inc.student_identifier || "Unnamed student"} (currently ${inc.status})`}
                  >
                    {statusLabel(inc.status)}
                    <ChevronDown className="w-3 h-3 opacity-60" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuLabel className="text-xs text-gray-500 font-normal">
                    Change status
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {STATUS_OPTIONS.map((opt) => (
                    <DropdownMenuItem
                      key={opt.value}
                      disabled={opt.value === inc.status}
                      onSelect={(e) => {
                        e.preventDefault();
                        if (opt.value !== inc.status) {
                          onStatusChange(inc.id, opt.value);
                        }
                      }}
                      className="text-sm"
                    >
                      <span className="flex items-center justify-between w-full">
                        <span>{opt.label}</span>
                        {opt.value === inc.status && (
                          <CheckCircle2 className="w-3.5 h-3.5 text-bodhi-teal" />
                        )}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div
                className={cn(
                  "text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 mr-1",
                  statusClasses(inc.status),
                )}
              >
                {statusLabel(inc.status)}
              </div>
            )}

            {onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(inc.id);
                }}
                className="flex-shrink-0 p-2 mr-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Remove incident"
                aria-label={`Remove incident for ${inc.student_identifier || "Unnamed student"}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {hasMore && (
        <button
          onClick={onToggleShowAll}
          className="mt-4 w-full flex items-center justify-center gap-1 text-sm text-bodhi-teal hover:text-bodhi-teal/80 py-2 transition-colors"
        >
          {showAll ? (
            <>Show less <ChevronUp className="w-4 h-4" /></>
          ) : (
            <>Show all incidents <ChevronDown className="w-4 h-4" /></>
          )}
        </button>
      )}
    </>
  );
};
